/**
 * Explicit avatar appearance chosen by a participant.
 *
 * A choice is optional: when a participant has not picked anything we fall back
 * to the deterministic peerId hash, so every client still agrees on how an
 * un-customised avatar looks.
 */
import {
  AVATAR_COLORS,
  AVATAR_EMOJIS,
  getColorForPeerId,
  getEmojiForPeerId,
} from "@/engine/state/avatarEmoji";

export interface AvatarChoice {
  /** Animal emoji from AVATAR_EMOJIS, or null for "auto". */
  emoji: string | null;
  /** Hex color from AVATAR_COLORS, or null for "auto". */
  color: string | null;
}

export const EMPTY_AVATAR_CHOICE: AvatarChoice = { emoji: null, color: null };

export function isSelectableEmoji(value: unknown): value is string {
  return typeof value === "string" && AVATAR_EMOJIS.includes(value);
}

export function isSelectableColor(value: unknown): value is string {
  return (
    typeof value === "string" && AVATAR_COLORS.includes(value.toUpperCase())
  );
}

/** Normalises anything read from storage or the network into a safe choice. */
export function normalizeAvatarChoice(raw: unknown): AvatarChoice {
  if (!raw || typeof raw !== "object") return EMPTY_AVATAR_CHOICE;
  // A missing key reads back as `undefined`, exactly as member access on a
  // `Partial<AvatarChoice>` did, without asserting `raw`'s shape.
  const rawEmoji = Reflect.get(raw, "emoji");
  const rawColor = Reflect.get(raw, "color");
  const color = typeof rawColor === "string" ? rawColor.toUpperCase() : null;
  return {
    emoji: isSelectableEmoji(rawEmoji) ? rawEmoji : null,
    color: color && isSelectableColor(color) ? color : null,
  };
}

/** Chosen emoji when valid, otherwise the deterministic per-peer default. */
export function resolveAvatarEmoji(peerId: string, chosen?: unknown): string {
  return isSelectableEmoji(chosen) ? chosen : getEmojiForPeerId(peerId);
}

/** Chosen color when valid, otherwise the deterministic per-peer default. */
export function resolveAvatarColor(peerId: string, chosen?: unknown): string {
  if (typeof chosen === "string" && isSelectableColor(chosen.toUpperCase())) {
    return chosen.toUpperCase();
  }
  return getColorForPeerId(peerId);
}

/**
 * Random animal + color pair. Avoids handing back the exact same combination
 * as `current`, so pressing shuffle always visibly changes something.
 */
export function randomizeAvatarChoice(
  current: AvatarChoice = EMPTY_AVATAR_CHOICE,
  random: () => number = Math.random,
): AvatarChoice {
  const pick = <T>(list: readonly T[], exclude: T | null): T => {
    // Both exported avatar palettes contain several choices, so excluding the
    // current value always leaves a visible alternative.
    const options = list.filter((item) => item !== exclude);
    return options[
      Math.min(options.length - 1, Math.floor(random() * options.length))
    ];
  };
  return {
    emoji: pick(AVATAR_EMOJIS, current.emoji),
    color: pick(AVATAR_COLORS, current.color),
  };
}
